import { Form } from "antd";
import { NamePath } from "antd/lib/form/interface";
import { useEffect, useMemo } from "react";
import { LogItem, LogTimeByDateResponse } from "@lhd/types";
import { LOG_TYPE } from "@lhd/features/TrackingTime/HomeTracking/contants";
import { buildTrackingTimelineRows } from "@lhd/features/TrackingTime/timelineRows";
import { TIMELINE_ITEMS } from "./config";
import TextField from "./fields/TextField";
import TimeField from "./fields/TimeField";
import LocationField from "./fields/LocationField";
import AttachmentsField from "./fields/AttachmentsField";
import { Styles } from "./styled";
import { ItemField } from "./types";

const hasLoggedAtValue = (loggedAt: unknown): boolean =>
  typeof loggedAt === "string" && loggedAt.trim().length > 0;

const resolveRowLoggedAtEntered = (
  logtime: LogItem,
  formValues: Record<string, unknown> | undefined,
): boolean => {
  const patch = formValues?.[String(logtime.id)];
  if (patch != null && typeof patch === "object" && !Array.isArray(patch)) {
    const loggedAt = (patch as { logged_at?: unknown }).logged_at;
    if (hasLoggedAtValue(loggedAt)) return true;
  }
  return hasLoggedAtValue(logtime.logged_at);
};

const findCycleCheckinRow = (
  timelineLogtimes: LogItem[],
  fromIndex: number,
): LogItem | null => {
  for (let i = fromIndex + 1; i < timelineLogtimes.length; i++) {
    if (timelineLogtimes[i].log_type === LOG_TYPE.CHECKIN) {
      return timelineLogtimes[i];
    }
  }
  return null;
};

const resolveFieldLabelText = (
  field: ItemField,
  logtime: LogItem | null,
): string | undefined => {
  if (field.type === "time" && logtime?.log_type === LOG_TYPE.CHECKIN) {
    return "出勤";
  }
  if (field.type === "time" && logtime?.log_type === LOG_TYPE.CHECKOUT) {
    return hasLoggedAtValue(logtime.logged_at) ? "退勤" : "退勤（未完了）";
  }
  return field.label;
};

const fieldLabelClassName = (
  field: ItemField,
  logtime: LogItem | null,
): string => {
  if (
    field.type === "time" &&
    (logtime?.log_type === LOG_TYPE.CHECKIN ||
      logtime?.log_type === LOG_TYPE.CHECKOUT)
  ) {
    return hasLoggedAtValue(logtime.logged_at)
      ? "item-label item-label--timeline-time-complete"
      : "item-label item-label--timeline-time-incomplete";
  }
  return "item-label";
};

interface TimelineProps {
  className?: string;
  data: LogTimeByDateResponse;
  editable?: boolean;
  canEditAllFields?: boolean;
}

const aggregateDayMemo = (logtimes: LogItem[]): string => {
  const hasText = (value: unknown): boolean =>
    typeof value === "string" ? value.trim().length > 0 : value != null;
  const noteTypeRows = logtimes.filter(
    (lt) => lt.log_type === LOG_TYPE.NOTE && hasText(lt.note),
  );
  const noteRows = noteTypeRows.length
    ? noteTypeRows
    : logtimes.filter((lt) => hasText(lt.note));
  if (!noteRows.length) return "";
  const sorted = [...noteRows].sort(
    (a, b) =>
      new Date(a.logged_at || a.created_at).getTime() -
      new Date(b.logged_at || b.created_at).getTime(),
  );
  const primary = sorted[sorted.length - 1];
  const text = primary?.note != null ? String(primary.note).trim() : "";
  return text;
};

const Timeline = (props: TimelineProps) => {
  const { className, data, editable = false, canEditAllFields = false } = props;
  const form = Form.useFormInstance();
  const canEditNoteAndAttachments = editable;
  const canEditTimeField = editable && canEditAllFields;

  const timelineLogtimes = useMemo(
    () => buildTrackingTimelineRows(data),
    [data],
  );

  const formValues = Form.useWatch([], form) as
    | Record<string, unknown>
    | undefined;

  const dayMemo = useMemo(
    () => (data?.logtimes?.length ? aggregateDayMemo(data.logtimes) : ""),
    [data?.logtimes],
  );

  const noteConfig = TIMELINE_ITEMS.note;

  useEffect(() => {
    if (!data) return;

    const values: Record<string, unknown> = {
      memo: dayMemo,
    };

    timelineLogtimes.forEach((logtime) => {
      const key = String(logtime.id);
      const withAttachments =
        logtime.log_type === LOG_TYPE.CHECKIN ||
        logtime.log_type === LOG_TYPE.CHECKOUT;
      values[key] = {
        logged_at: logtime.logged_at,
        ...(withAttachments
          ? {
              attachments: logtime.attachments ?? [],
            }
          : {}),
      };
    });

    form.setFieldsValue(values);
  }, [data, dayMemo, form, timelineLogtimes]);

  const memoReadonly = dayMemo;

  const isBreakTimeFieldDisabled = (
    logtime: LogItem,
    rowIndex: number,
  ): boolean => {
    if (
      logtime.log_type !== LOG_TYPE.BREAK_START &&
      logtime.log_type !== LOG_TYPE.BREAK_END
    ) {
      return false;
    }
    const cycleCheckin = findCycleCheckinRow(timelineLogtimes, rowIndex);
    if (!cycleCheckin) return true;
    return !resolveRowLoggedAtEntered(cycleCheckin, formValues);
  };

  const renderField = (
    field: ItemField,
    logtime: LogItem | null,
    rowIndex?: number,
  ) => {
    const name: NamePath =
      logtime == null ? field.name : [String(logtime.id), field.name];

    if (field.type === "text") {
      return (
        <TextField
          name={name}
          editable={canEditNoteAndAttachments}
          readonlyText={memoReadonly}
        />
      );
    }
    if (field.type === "time") {
      const attendanceTimeIncomplete =
        (logtime?.log_type === LOG_TYPE.CHECKIN ||
          logtime?.log_type === LOG_TYPE.CHECKOUT) &&
        !hasLoggedAtValue(logtime.logged_at);
      const breakTimeDisabled =
        logtime != null &&
        rowIndex != null &&
        isBreakTimeFieldDisabled(logtime, rowIndex);
      return (
        <TimeField
          name={name}
          editable={canEditTimeField}
          disabled={breakTimeDisabled}
          readonlyIso={logtime?.logged_at ?? null}
          workDate={data.date}
          emptyLabel={attendanceTimeIncomplete ? "--:--" : undefined}
        />
      );
    }
    if (field.type === "location" && logtime) {
      return <LocationField logtime={logtime} />;
    }
    if (field.type === "attachments" && logtime) {
      return (
        <AttachmentsField
          logId={String(logtime.id)}
          readonlyAttachments={logtime.attachments ?? []}
          editable={canEditNoteAndAttachments}
        />
      );
    }
    return null;
  };

  return (
    <Styles className={className ?? ""}>
      <div className="timeline-note-row">
        <div className="container-item-field">
          <div className="left">
            <div
              className={`item-icon ${!noteConfig.icon ? "empty" : ""}`}
              style={{ background: noteConfig.background }}
            >
              {noteConfig.icon}
            </div>
            {!noteConfig.isNotShowLine && <div className="line" />}
          </div>
          <div className="note-body">
            {noteConfig.fields.map((field) => {
              const labelText = resolveFieldLabelText(field, null);
              return (
                <div key={field.name}>
                  {renderField(field, null)}
                  {labelText ? (
                    <p className={fieldLabelClassName(field, null)}>
                      {labelText}
                    </p>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {timelineLogtimes.map((logtime, index) => {
        const item = TIMELINE_ITEMS[logtime.log_type];
        if (!item) return null;

        const showConnectorLine = index < timelineLogtimes.length - 1;

        return (
          <div key={logtime.id}>
            <div className="container-item-field">
              <div className="left">
                <div
                  className={`item-icon ${!item.icon ? "empty" : ""}`}
                  style={{ background: item.background }}
                >
                  {item.icon}
                </div>
                {showConnectorLine && <div className="line" />}
              </div>
              <div>
                {item.fields.map((field) => {
                  const labelText = resolveFieldLabelText(field, logtime);
                  return (
                    <div key={`${logtime.id}-${field.name}`}>
                      {renderField(field, logtime, index)}
                      {labelText ? (
                        <p className={fieldLabelClassName(field, logtime)}>
                          {labelText}
                        </p>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })}
    </Styles>
  );
};

export default Timeline;
